import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { getPracticeManagementDiff } from "services";

import { PRACTICE_MANAGEMENT_TABS, PRACTICE_MANAGEMENT_VERSION_DIFFERENCES } from "consts";

export type ChangedField = { field: string; before: unknown; after: unknown };
export type ChangedItem = { identity: Record<string, unknown>; changedFields: ChangedField[] };
export type DiffResult = {
  added: number;
  modified: number;
  deleted: number;
  hasChanges: boolean;
  changedItems: ChangedItem[];
  addedItems: Record<string, unknown>[];
  removedItems: Record<string, unknown>[];
};

const STALE_TIME = 5 * 60 * 1000;

type PracticeManagementTabKey = (typeof PRACTICE_MANAGEMENT_TABS)[number]["key"];

// Parameters are unversioned. This object maps the tab key to the collection
// in the database. Is this needed? The backend already maintains this LUT and
// should be able to do it in place
const DIFF_COLLECTION_KEY_BY_TAB_KEY: Partial<Record<PracticeManagementTabKey, string>> = {
  "crop-emission-factors": "cropemissionfactors",
  "mineral-fertilizer-emission-factors": "fertilizermineralemissionsfactors",
  "organic-fertilizer-emission-factors": "fertilizerorganicemissionsfactors",
  "plant-protection-emission-factors": "plantprotectionemissionsfactors",
  "seeding-emission-factors": "seedingemissionsfactors",
  "tillage-emission-factors": "tillageemissionsfactors",
  "soil-emission-factors": "soilemissionsfactors",
  "utility-emission-factors": "utilityemissionsfactors",
};

const getChangedFields = (before: Record<string, unknown>, after: Record<string, unknown>): ChangedField[] =>
  Object.keys(after)
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => ({ field: k, before: before[k], after: after[k] }));

export const usePracticeManagementDiff = (fromVersion: string | undefined, toVersion: string | undefined, active: boolean) => {
  const enabled = active && !!fromVersion && !!toVersion;

  const diffQuery = useQuery({
    queryKey: [PRACTICE_MANAGEMENT_VERSION_DIFFERENCES, fromVersion, toVersion],
    queryFn: () => getPracticeManagementDiff(fromVersion!, toVersion!),
    enabled,
    staleTime: STALE_TIME,
  });

  const isLoadingDiff = enabled && diffQuery.isLoading;

  const endpointSummaries = useMemo(() => {
    const collections = diffQuery.data?.collections ?? {};
    return PRACTICE_MANAGEMENT_TABS.map(({ key, label }) => {
      const col = collections[DIFF_COLLECTION_KEY_BY_TAB_KEY[key] ?? ""];
      if (!col) return { label, diff: { added: 0, modified: 0, deleted: 0, hasChanges: false, changedItems: [], addedItems: [], removedItems: [] } };

      const added = col.added.length;
      const modified = col.changed.length;
      const deleted = col.removed.length;
      const changedItems: ChangedItem[] = col.changed.map((c) => ({
        identity: c.identity,
        changedFields: getChangedFields(c.before, c.after),
      }));
      return {
        label,
        diff: {
          added,
          modified,
          deleted,
          hasChanges: added > 0 || modified > 0 || deleted > 0,
          changedItems,
          addedItems: col.added,
          removedItems: col.removed,
        },
      };
    });
  }, [diffQuery.data]);

  return { isLoadingDiff, endpointSummaries };
};
